if (!customElements.get('bt-campaign-banners')) {
  class BtCampaignBanners extends HTMLElement {
    constructor() {
      super();
      this.track = null;
      this.prevBtn = null;
      this.nextBtn = null;
      this.dots = [];
      this.slides = [];
      this.autoplayTimer = null;
      this.autoplay = false;
      this.autoplayInterval = 5000;
      this.mobileLayout = 'stack';
      this.isHovered = false;
      this.scrollRaf = null;
    }

    connectedCallback() {
      this.track = this.querySelector('[data-bt-cb-track]');
      if (!this.track) return;

      this.prevBtn = this.querySelector('[data-bt-cb-prev]');
      this.nextBtn = this.querySelector('[data-bt-cb-next]');
      this.dots = Array.from(this.querySelectorAll('[data-bt-cb-dot]'));
      this.slides = Array.from(this.track.querySelectorAll('[data-bt-cb-slide]'));

      this.autoplay = this.dataset.autoplay === 'true';
      this.autoplayInterval = parseInt(this.dataset.autoplayInterval, 10) || 5000;
      this.autoplayDirection = this.dataset.autoplayDirection === 'left' ? -1 : 1;
      this.mobileLayout = this.dataset.mobileLayout || 'stack';

      this.onScroll = this.onScroll.bind(this);
      this.track.addEventListener('scroll', this.onScroll, { passive: true });

      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', () => this.scrollByPage(-1));
      }
      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', () => this.scrollByPage(1));
      }

      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          const index = parseInt(dot.dataset.btCbDot, 10);
          this.scrollToIndex(index);
        });
      });

      this.addEventListener('mouseenter', () => {
        this.isHovered = true;
        this.pauseAutoplay();
      });
      this.addEventListener('mouseleave', () => {
        this.isHovered = false;
        this.startAutoplay();
      });

      this.updateArrows();
      this.updateDots();
      this.startAutoplay();

      if (typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(() => {
          this.updateArrows();
          this.updateDots();
        });
        this.resizeObserver.observe(this.track);
      }
    }

    disconnectedCallback() {
      this.pauseAutoplay();
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.track) this.track.removeEventListener('scroll', this.onScroll);
    }

    onScroll() {
      if (this.scrollRaf) cancelAnimationFrame(this.scrollRaf);
      this.scrollRaf = requestAnimationFrame(() => {
        this.updateArrows();
        this.updateDots();
      });
    }

    isMobileStacked() {
      return this.mobileLayout === 'stack' && window.matchMedia('(max-width: 749px)').matches;
    }

    getPageWidth() {
      if (!this.slides.length) return this.track.clientWidth;
      const first = this.slides[0];
      const styles = window.getComputedStyle(this.track);
      const gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
      return first.getBoundingClientRect().width + gap;
    }

    getVisibleCount() {
      if (!this.slides.length) return 1;
      const slideWidth = this.slides[0].getBoundingClientRect().width;
      if (slideWidth <= 0) return 1;
      return Math.max(1, Math.round(this.track.clientWidth / slideWidth));
    }

    scrollByPage(direction) {
      if (this.isMobileStacked()) return;
      const pageWidth = this.getPageWidth() * this.getVisibleCount();
      this.track.scrollBy({ left: pageWidth * direction, behavior: 'smooth' });
    }

    scrollToIndex(index) {
      if (this.isMobileStacked()) return;
      const slide = this.slides[index];
      if (!slide) return;
      const left = slide.offsetLeft - this.track.offsetLeft;
      this.track.scrollTo({ left, behavior: 'smooth' });
    }

    getActiveIndex() {
      if (!this.slides.length) return 0;
      const trackLeft = this.track.scrollLeft;
      let bestIndex = 0;
      let bestDiff = Infinity;
      this.slides.forEach((slide, i) => {
        const diff = Math.abs(slide.offsetLeft - this.track.offsetLeft - trackLeft);
        if (diff < bestDiff) {
          bestDiff = diff;
          bestIndex = i;
        }
      });
      return bestIndex;
    }

    updateArrows() {
      if (!this.prevBtn && !this.nextBtn) return;
      if (this.isMobileStacked()) {
        if (this.prevBtn) this.prevBtn.setAttribute('disabled', '');
        if (this.nextBtn) this.nextBtn.setAttribute('disabled', '');
        return;
      }
      const atStart = this.track.scrollLeft <= 2;
      const atEnd = this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 2;
      if (this.prevBtn) {
        atStart ? this.prevBtn.setAttribute('disabled', '') : this.prevBtn.removeAttribute('disabled');
      }
      if (this.nextBtn) {
        atEnd ? this.nextBtn.setAttribute('disabled', '') : this.nextBtn.removeAttribute('disabled');
      }
    }

    updateDots() {
      if (!this.dots.length) return;
      const active = this.getActiveIndex();
      this.dots.forEach((dot, i) => {
        dot.setAttribute('data-active', i === active ? 'true' : 'false');
      });
    }

    startAutoplay() {
      if (!this.autoplay || this.isHovered) return;
      if (this.slides.length < 2) return;
      this.pauseAutoplay();
      this.autoplayTimer = window.setInterval(() => {
        if (this.isMobileStacked()) return;
        if (this.autoplayDirection === 1) {
          const atEnd = this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 2;
          if (atEnd) {
            this.track.scrollTo({ left: 0, behavior: 'smooth' });
          } else {
            this.scrollByPage(1);
          }
        } else {
          const atStart = this.track.scrollLeft <= 2;
          if (atStart) {
            this.track.scrollTo({ left: this.track.scrollWidth, behavior: 'smooth' });
          } else {
            this.scrollByPage(-1);
          }
        }
      }, this.autoplayInterval);
    }

    pauseAutoplay() {
      if (this.autoplayTimer) {
        clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    }
  }

  customElements.define('bt-campaign-banners', BtCampaignBanners);
}
